import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Bell, Send, Users, User } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface NotificationsCenterProps {
  onSendNotification: (type: string, title: string, message: string, recipientId?: string) => Promise<void>;
  users: Array<{ id: string; name: string; type: string }>;
}

const NotificationsCenter = ({ onSendNotification, users }: NotificationsCenterProps) => {
  const [notificationForm, setNotificationForm] = useState({
    type: 'all',
    title: '',
    message: '',
    recipientId: ''
  });
  const { toast } = useToast();

  const handleSendNotification = async () => {
    if (!notificationForm.title || !notificationForm.message) {
      toast({
        title: "خطأ",
        description: "يرجى ملء جميع الحقول المطلوبة",
        variant: "destructive"
      });
      return;
    }

    if (notificationForm.type === 'individual' && !notificationForm.recipientId) {
      toast({
        title: "خطأ",
        description: "يرجى اختيار المستقبل للرسالة الفردية",
        variant: "destructive"
      });
      return;
    }

    // للإشعارات الجماعية للملاك أو الباحثين، لا نحتاج recipient_id
    const recipientId = notificationForm.type === 'individual' ? notificationForm.recipientId : undefined;

    await onSendNotification(
      notificationForm.type,
      notificationForm.title,
      notificationForm.message,
      recipientId
    );

    // Reset form
    setNotificationForm({
      type: 'all',
      title: '',
      message: '',
      recipientId: ''
    });
  };

  return (
    <Card className="shadow-lg">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          مركز الإشعارات
        </CardTitle>
        <CardDescription>إرسال إشعارات وتنبيهات للمستخدمين</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Notification Type */}
        <div className="space-y-2">
          <label className="text-sm font-medium">نوع الإشعار</label>
          <Select 
            value={notificationForm.type} 
            onValueChange={(value) => setNotificationForm({...notificationForm, type: value, recipientId: ''})}
          >
            <SelectTrigger>
              <SelectValue placeholder="اختر نوع الإشعار" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  جميع المستخدمين
                </div>
              </SelectItem>
              <SelectItem value="landlord">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  الملاك فقط
                </div>
              </SelectItem>
              <SelectItem value="tenant">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  الباحثين فقط
                </div>
              </SelectItem>
              <SelectItem value="service_provider">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  أصحاب الخدمات فقط
                </div>
              </SelectItem>
              <SelectItem value="individual">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  مستخدم محدد
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Individual User Selection */}
        {notificationForm.type === 'individual' && (
          <div className="space-y-2">
            <label className="text-sm font-medium">المستقبل</label>
            <Select 
              value={notificationForm.recipientId} 
              onValueChange={(value) => setNotificationForm({...notificationForm, recipientId: value})}
            >
              <SelectTrigger>
                <SelectValue placeholder="اختر المستخدم" />
              </SelectTrigger>
              <SelectContent>
                {users.map((user) => (
                  <SelectItem key={user.id} value={user.id}>
                    {user.name} ({user.type})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Notification Title */}
        <div className="space-y-2">
          <label className="text-sm font-medium">عنوان الإشعار</label>
          <Input
            placeholder="أدخل عنوان الإشعار..."
            value={notificationForm.title}
            onChange={(e) => setNotificationForm({...notificationForm, title: e.target.value})}
            className="text-right"
          />
        </div>

        {/* Notification Message */}
        <div className="space-y-2">
          <label className="text-sm font-medium">محتوى الإشعار</label>
          <Textarea
            placeholder="أدخل محتوى الإشعار..."
            value={notificationForm.message}
            onChange={(e) => setNotificationForm({...notificationForm, message: e.target.value})}
            className="text-right min-h-[120px]"
            rows={5}
          />
        </div>

        {/* Send Button */}
        <Button 
          onClick={handleSendNotification} 
          className="w-full"
          size="lg"
        >
          <Send className="h-4 w-4 mr-2" />
          إرسال الإشعار
        </Button>

        {/* Quick Templates */}
        <div className="border-t pt-4">
          <h4 className="text-sm font-medium mb-3">قوالب سريعة</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setNotificationForm({
                ...notificationForm,
                title: 'تحديث في النظام',
                message: 'تم تحديث النظام بميزات جديدة. يرجى مراجعة التحديثات.'
              })}
            >
              تحديث النظام
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setNotificationForm({
                ...notificationForm,
                title: 'تذكير بالاشتراك',
                message: 'ينتهي اشتراكك قريباً. يرجى التجديد لضمان استمرار الخدمة.'
              })}
            >
              تذكير اشتراك
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setNotificationForm({
                ...notificationForm,
                title: 'ترحيب بالمستخدمين الجدد',
                message: 'مرحباً بك في منصة كنف! نتمنى لك تجربة رائعة.'
              })}
            >
              رسالة ترحيب
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setNotificationForm({
                ...notificationForm,
                title: 'صيانة مجدولة',
                message: 'سيتم إجراء صيانة للنظام. قد تواجه انقطاع مؤقت في الخدمة.'
              })}
            >
              إشعار صيانة
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default NotificationsCenter;